import { type ReactNode } from "react";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ResponseRenderer from "../../layouts/report/ui/ResponseRenderer";
import EmptyWidgetState from "../EmptyWidgetState";
import type { WidgetExecuteResponse } from "../../types/widget";
import type { ChatOrchestrationWebSocketPayload } from "../../services/chatService";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  title?: string;
  result?: WidgetExecuteResponse;
  loading?: boolean;
  onChartClick?: (categoryValue: string) => void;
};

function toPayload(result: WidgetExecuteResponse): ChatOrchestrationWebSocketPayload {
  const format = (result.format || "text").toLowerCase();
  let type: ChatOrchestrationWebSocketPayload["type"] = "text";
  if (format === "html") type = "html";
  else if (format === "table") type = "table";
  else if (format === "echarts" || format === "chart") type = "chart";
  else if (format === "json") type = "json";
  else if (format === "error") type = "error";

  return {
    correlationId: "widget",
    type,
    outputFormat: result.format,
    result: result.payload,
    error: result.error,
    timestamp: new Date().toISOString(),
  };
}

function TileShell({
  title,
  loading,
  children,
}: {
  title?: string;
  loading?: boolean;
  children: ReactNode;
}) {
  const custom = useAppCustomStyles();

  return (
    <Paper elevation={0} sx={custom.components.widgetOutputView.tileRoot}>
      {loading && (
        <Box sx={custom.components.widgetOutputView.loadingOverlay}>
          <CircularProgress size={24} />
        </Box>
      )}
      {title && (
        <Box sx={custom.components.widgetOutputView.titleBar}>
          <Typography variant="subtitle2" fontWeight={700} sx={custom.components.widgetOutputView.titleText}>
            {title}
          </Typography>
        </Box>
      )}
      <Box sx={custom.components.widgetOutputView.contentBox}>{children}</Box>
    </Paper>
  );
}

function humanizeWarning(code: string, t: (key: string) => string): string {
  if (code.startsWith("AGG_EMPTY_CHILD_BUCKETS")) {
    return t("widget.warning.emptyChildBuckets");
  }
  if (code.startsWith("TABLE_NO_ROWS")) {
    return t("widget.warning.tableNoRows");
  }
  if (code.startsWith("CHART_NO_DATA")) {
    return t("widget.warning.chartNoData");
  }
  if (code.startsWith("ADAPTOR_CHART_BINDING")) {
    return t("widget.warning.adaptorChartBinding");
  }
  return code;
}

export default function WidgetOutputView({ title, result, loading = false, onChartClick }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();

  if (loading && !result) {
    return (
      <TileShell title={title} loading>
        <Box sx={custom.components.widgetOutputView.emptyContent} />
      </TileShell>
    );
  }

  if (!result && !loading) {
    return (
      <TileShell title={title}>
        <EmptyWidgetState title={t("dashboard.noWidgetData")} />
      </TileShell>
    );
  }

  if (result?.error || result?.format?.toLowerCase() === "error") {
    return (
      <TileShell title={title}>
        <Alert severity="error">
          {result.error || t("widget.error.generic")}
        </Alert>
      </TileShell>
    );
  }

  const warnings = result?.warnings?.filter(Boolean) ?? [];

  return (
    <TileShell title={title} loading={loading && !!result}>
      {warnings.length > 0 && (
        <Alert severity="warning" sx={custom.components.widgetOutputView.warningsAlert}>
          {warnings.map((w) => (
            <Typography key={w} variant="body2" component="div">
              {humanizeWarning(w, t)}
            </Typography>
          ))}
        </Alert>
      )}
      {result && (
        <ResponseRenderer payload={toPayload(result)} fitContainer onChartClick={onChartClick} />
      )}
    </TileShell>
  );
}
